// =============================================================================
// GRAPHIQUES PANEL EXPANDED - Panneau déplié avec onglets
// =============================================================================

import React, { useMemo } from 'react';
import { X, TrendingUp, BarChart3, PieChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChantierRentabilite } from '@/hooks/useChantiersFiltresRentabilite';
import { useGlobalChartData } from '@/hooks/useGlobalChartData';
import { useGlobalDetailedCosts } from '@/hooks/useGlobalDetailedCosts';
import { EvolutionGlobaleChart } from '../charts/EvolutionGlobaleChart';
import { ComparaisonChantiersChart } from '../charts/ComparaisonChantiersChart';
import { RepartitionGlobaleChart } from '../charts/RepartitionGlobaleChart';

type TabId = 'evolution' | 'comparaison' | 'repartition';

interface GraphiquesPanelExpandedProps {
  isVisible: boolean;
  onClose: () => void;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  chantiers: ChantierRentabilite[];
}

// Configuration des onglets
const TABS = [
  {
    id: 'evolution' as TabId,
    label: 'Évolution',
    shortLabel: 'Évol.',
    icon: TrendingUp,
    description: 'Vue d\'ensemble',
  },
  {
    id: 'comparaison' as TabId,
    label: 'Comparaison',
    shortLabel: 'Comp.',
    icon: BarChart3,
    description: 'Par chantier',
  },
  {
    id: 'repartition' as TabId,
    label: 'Répartition',
    shortLabel: 'Répart.',
    icon: PieChart,
    description: 'Coûts globaux',
  },
];

/**
 * Panneau déplié des Graphiques globaux
 */
const GraphiquesPanelExpanded = ({
  isVisible,
  onClose,
  activeTab,
  onTabChange,
  chantiers,
}: GraphiquesPanelExpandedProps) => {
  // Charger les données globales
  const { comparaisonChantiers, totaux } = useGlobalChartData(chantiers);

  // Extraire les IDs des chantiers pour le hook des coûts détaillés
  const chantierIds = useMemo(() => chantiers.map(c => c.id), [chantiers]);

  // Charger les coûts détaillés (MO + Fournisseurs + Frais par type)
  const { detailedCosts, totalCouts } = useGlobalDetailedCosts(chantierIds);

  if (!isVisible) return null;

  // Rendu du graphique selon l'onglet actif
  const renderChart = () => {
    switch (activeTab) {
      case 'evolution':
        return <EvolutionGlobaleChart totaux={totaux} />;
      case 'comparaison':
        return <ComparaisonChantiersChart data={comparaisonChantiers} />;
      case 'repartition':
        return <RepartitionGlobaleChart data={detailedCosts} totalCouts={totalCouts} />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* ═══ OVERLAY ═══ */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* ═══ PANNEAU ═══ */}
      <div
        className={cn(
          'fixed right-0 top-0 bottom-0 z-50',
          // Largeur responsive
          'w-[95vw] sm:w-[85vw] md:w-[75vw] lg:w-[65vw]',
          'max-w-[1200px] min-w-[350px]',
          // Style visuel
          'bg-gradient-to-b from-background to-background/98',
          'border-l border-border/50',
          'shadow-2xl shadow-black/50',
          // Animation d'entrée
          'animate-in slide-in-from-right duration-300'
        )}
      >
        {/* ═══ HEADER ═══ */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
          <div>
            <h2 className="text-xl font-bold text-foreground">
              Analyse financière globale
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {totaux.nombreChantiers} chantier{totaux.nombreChantiers > 1 ? 's' : ''} analysé{totaux.nombreChantiers > 1 ? 's' : ''}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 rounded-full hover:bg-destructive/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* ═══ ONGLETS ═══ */}
        <div className="px-6 py-4 border-b border-border/30">
          <div className="flex gap-2 p-1.5 bg-muted/30 rounded-xl">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg',
                    'text-sm font-medium transition-all duration-200',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    isActive
                      ? 'bg-card text-foreground shadow-lg shadow-black/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                  )}
                >
                  <Icon className={cn(
                    'h-4 w-4 flex-shrink-0 transition-colors',
                    isActive && 'text-primary'
                  )} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ═══ CONTENU GRAPHIQUE ═══ */}
        <div className="flex-1 overflow-y-auto p-6">
          <div
            className={cn(
              'min-h-[500px] rounded-xl',
              'bg-gradient-to-br from-card/50 to-card/30',
              'border border-border/30',
              'p-6'
            )}
          >
            {renderChart()}
          </div>
        </div>
      </div>
    </>
  );
};

export default GraphiquesPanelExpanded;
